'use client';

import { createContext, HTMLProps, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Link, { LinkProps } from 'next/link';

import Dropdown from '@/components/V2/DropdownMenu';
import { useDetectClickOutside, useWindowDimensions } from '@/helpers/hooks';
import { useDebouncedValue } from '@/helpers/hooks/useDebounced';
import useWindowScroll from '@/helpers/hooks/useWindowScroll';
import cn from '@/helpers/utils/classNames';
import { SearchOutlined } from '@mui/icons-material';

import Button from '../../Button';
import TextInput from '../../TextInput';
import AutoCompleteWrapper from '../SearchBar/AutoCompleteWrapper';
import { useSearchContext } from '../SearchBar/SearchBarProvider';

const Cart = dynamic(() => import('./Cart'));

const MenuBarContext = createContext<string | null>(null);
export function useMenuBarContext() {
  const context = useContext(MenuBarContext);
  if (!context) throw new Error('MenuBar.* can only be used within MenuBar component.');

  return context;
}

export interface MenuBarProps {
  children: ReactNode;
}

function Bootsrap(props: { children: ReactNode }) {
  const { children } = props;
  const menuRef = useRef<HTMLDivElement | null>(null);
  const shallowMenuRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const { width } = useWindowDimensions();

  const {
    value,
    onSearch,
    onSearchChange,
    getInputProps,
    autocompleteState,
    closeAutoCompleteMenu,
    isSearchBarFocused,
    setSearchBarFocused,
  } = useSearchContext();
  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = getInputProps({
    inputElement: null,
  });

  const makeNavbarSticky = useCallback(() => {
    const rect = shallowMenuRef.current?.getBoundingClientRect();
    if (rect && rect.top <= 0) {
      setIsSticky(true);
    } else if (rect && rect.top > 0) {
      setIsSticky(false);
      setShowSearchBar(false);
    }
  }, []);

  useWindowScroll(() => {
    makeNavbarSticky();
  }, true);

  useDetectClickOutside(() => {
    if (showSearchBar) {
      setShowSearchBar(false);
    }
  }, menuRef);

  useEffect(() => {
    if (width > 768) makeNavbarSticky();
  }, [width, makeNavbarSticky]);

  const handleSearch = () => {
    // if (showSearchBar) {
    //   onSearch();
    // }
    setSearchBarFocused(!isSearchBarFocused);
    setShowSearchBar(!showSearchBar);
  };

  const debouncedValue = useDebouncedValue(value, 500);
  useEffect(() => {
    onChange({
      target: { value: debouncedValue },
      currentTarget: { value: debouncedValue },
    } as unknown as Event);
  }, [debouncedValue]);

  return (
    <>
      <div ref={shallowMenuRef}></div>
      <div
        ref={menuRef}
        className={cn(
          'v2-flex-row v2-flex-wrap v2-items-center v2-overflow-x-clip v2-bg-white v2-py-[16px] sm:v2-hidden md:v2-hidden lg:v2-flex xs:v2-hidden xxs:v2-hidden',
          {
            'v2-top-0 v2-z-50 v2-py-[15px] v2-shadow-menu v2-fixed v2-left-0 v2-right-0': isSticky,
            'v2-justify-center': !showSearchBar,
            'md:v2-gap-[50px]': !showSearchBar,
          }
        )}
      >
        {isSticky && showSearchBar ? (
          <div className="v2-mx-auto v2-flex-grow v2-relative v2-w-full v2-max-w-[1080px]">
            <TextInput
              id="search-from-menubar"
              autoFocus
              value={value}
              type="text"
              className="v2-relative v2-w-full v2-p-0 v2-text-base"
              placeholder="Search"
              onChange={e => {
                onSearchChange(e);

                // const value = e.target.value?.trim();
                // handleDebouncedChange({
                //   ...e,
                //   target: { ...e.target, value },
                //   currentTarget: { ...(e.currentTarget || e.target), value },
                // } as unknown as Event);
              }}
              onClick={e => onClick(e as unknown as MouseEvent)}
              onFocus={e => {
                setSearchBarFocused(true);
                if (!isSearchBarFocused) onFocus(e as unknown as Event);
                if (value.length > 0) {
                  const target = { value };
                  const event = { target, currentTarget: target, relatedTarget: target };

                  onChange(event as unknown as Event);
                }
              }}
              // onBlur={() => setSearchBarFocused(false)}
              onKeyDown={e => {
                onKeyDown(e as unknown as KeyboardEvent);
                if (e.key === 'Enter') {
                  if (!value) return;
                  onSearch();
                  closeAutoCompleteMenu();
                  // setSearchBarFocused(false);
                }
              }}
              onCompositionEnd={e => onCompositionEnd(e as unknown as Event)}
            />

            {isSearchBarFocused && (
              <AutoCompleteWrapper className="v2-px-4 v2-pl-[60px]" containerClassName="!v2-top-[60px]" />
            )}
          </div>
        ) : (
          children
        )}
        {isSticky && (
          <>
            <div>
              <Button
                title="search"
                type="button"
                className={cn(
                  `${
                    showSearchBar && 'v2-relative v2-z-30 -v2-ml-12'
                  } v2-inline-block !v2-p-0 !v2-px-0 v2-text-sm hover:v2-bg-white hover:v2-text-primary v2-right-[30px] !v2-rounded-[4px] v2-h-[34px] v2-w-[34px]`,
                  { '!v2-rounded-l-none v2-absolute v2-right-[234px]': showSearchBar }
                )}
                scheme="primary"
                variant="filled"
                onClick={handleSearch}
                btnSize={'xs'}
              >
                <SearchOutlined className="!v2-text-xl" />
              </Button>
            </div>

            <div
              className={`v2-flex v2-cursor-pointer v2-items-center ${
                showSearchBar && 'v2-mt-4 v2-w-full v2-justify-center'
              }`}
            >
              <Cart />
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface RawMenuLinkProps {
  children: ReactNode;
  href: string;
}

export type MenuLinkProps = RawMenuLinkProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

function MenuLink(props: MenuLinkProps) {
  useMenuBarContext();

  const { children, as, ...restProps } = props;

  const Comp = as === 'a' ? 'a' : Link;

  return (
    <Comp
      className="v2-flex v2-items-center v2-whitespace-nowrap v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-duration-300 hover:v2-text-primary"
      {...restProps}
    >
      {children}
    </Comp>
  );
}

export default function MenuBar(props: MenuBarProps) {
  const { children } = props;

  return (
    <MenuBarContext.Provider value={'menubar'}>
      <Bootsrap>{children}</Bootsrap>
    </MenuBarContext.Provider>
  );
}

MenuBar.Link = MenuLink;
MenuBar.DropDown = Dropdown;
